import { Button, Modal, ProgressCircle } from '@lego/connect-components-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { TableSkeleton } from '@/components/table-skeleton';
import { PrintJobsTable } from '../../components';
import { PRINT_JOBS_PAGE_SIZE } from '../../constants/pagination';
import { useGraphUser } from '../../hooks/graph/use-graph-user';
import { useInfiniteList } from '../../hooks/use-infinite-list';
import { useIntersection } from '../../hooks/use-intersection';
import { useApi } from '../../providers/api';
import { useUserStore } from '../../stores/user-store';
import type { LabelData, PrinterData, PrintJobData } from '../../types/types';
import { usePrintData } from '../print/hooks';

const CreatedByDisplay = ({ oid }: { oid: string }) => {
  const { data } = useGraphUser(oid);
  return <span className="text-core-slate-1300">{data?.displayName ?? oid}</span>;
};

type PrintJobTableRow = PrintJobData & {
  displayId: number;
};

export const PrintJobsPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { client } = useApi();
  const { user } = useUserStore();
  const [selectedPrintJob, setSelectedPrintJob] = useState<PrintJobData | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const triggerRef = useRef<HTMLElement>(null);
  const fetchPrintJobsPage = useCallback(
    (query: { limit: string; offset: string }) =>
      client.v1['print-jobs'].$get({ query }).then((r) => r.json()),
    [client]
  );

  const {
    items: printJobItems,
    itemCount: printJobsTotal,
    isLoading: printJobsIsLoading,
    isLoadingMore: printJobsIsLoadingMore,
    loadMore: loadMorePrintJobs,
    refresh: refreshPrintJobs,
  } = useInfiniteList<PrintJobData>({
    cacheKey: 'print-jobs',
    pageSize: PRINT_JOBS_PAGE_SIZE,
    fetchPage: fetchPrintJobsPage,
  });

  useEffect(() => {
    refreshPrintJobs();
  }, [refreshPrintJobs]);

  const sentinelRef = useIntersection<HTMLDivElement>(loadMorePrintJobs);

  const { printers, labels, printersLoading, labelsLoading } = usePrintData();

  const printerMap = useMemo(() => {
    if (!printers?.items) return new Map<number, PrinterData>();
    return printers.items.reduce<Map<number, PrinterData>>((map, printer: PrinterData) => {
      map.set(printer.id, printer);
      return map;
    }, new Map<number, PrinterData>());
  }, [printers]);

  const labelMap = useMemo(() => {
    if (!labels?.items) return new Map<number, LabelData>();
    return labels.items.reduce<Map<number, LabelData>>((map, label: LabelData) => {
      map.set(label.id!, label);
      return map;
    }, new Map<number, LabelData>());
  }, [labels]);

  const handlePrintJobClick = (printJob: PrintJobData) => {
    triggerRef.current = document.activeElement as HTMLElement;
    setSelectedPrintJob(printJob);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => setIsDialogOpen(false);

  const isAppTeamUser = user.type === 'user';

  const tableData: PrintJobTableRow[] = useMemo(
    () => printJobItems.map((item, index) => ({ ...item, displayId: index + 1 })),
    [printJobItems]
  );

  const isInitialLoading =
    (printJobsIsLoading && printJobItems.length === 0) || printersLoading || labelsLoading;

  if (isInitialLoading) {
    return <TableSkeleton columns={8} page showAction />;
  }

  return (
    <>
      <div className="px-400 py-400">
        <div className="flex items-start justify-between mb-300">
          <div>
            <h1 className="text-heading-md font-brand font-bold text-core-slate-1300">
              {`${t('printJobsPage.display.printJobsTitle')} (${printJobsTotal})`}
            </h1>
          </div>
          <div>
            <Button
              variant="primary"
              size="small"
              onClick={() => navigate(user.type === 'operator' ? '/form' : '/')}
            >
              {t('navbar.display.newPrintJob')}
            </Button>
          </div>
        </div>
        <PrintJobsTable
          jobs={tableData}
          printerMap={printerMap}
          labelMap={labelMap}
          showId={true}
          showLabel={true}
          searchable={false}
          sortable={false}
          ariaLabel={t('printJobsPage.display.printJobsTitle')}
          totalCount={printJobsTotal}
          onRowClick={handlePrintJobClick}
        />
        <div
          ref={sentinelRef}
          className={`flex items-center justify-center ${printJobsIsLoadingMore ? 'pb-300' : 'h-px'}`}
        >
          {printJobsIsLoadingMore && (
            <ProgressCircle indeterminate size="small" aria-label="Loading" />
          )}
        </div>
      </div>

      <Modal
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        triggerRef={triggerRef}
        size="medium"
      >
        <Modal.Title>
          {isAppTeamUser
            ? t('printJobsPage.dialog.detailsTitle')
            : t('printJobsPage.dialog.detailsTitleWithId', { id: selectedPrintJob?.id })}
        </Modal.Title>
        <Modal.Content>
          {selectedPrintJob && (
            <div className="grid grid-cols-1 gap-200 text-body-xs">
              <div>
                <span className="font-brand font-bold text-core-slate-1100 block mb-50">
                  {t('printJobsPage.display.status')}:
                </span>
                <span className={selectedPrintJob.status}>
                  {selectedPrintJob.status.charAt(0).toUpperCase() +
                    selectedPrintJob.status.slice(1)}
                </span>
              </div>

              <div>
                <span className="font-brand font-bold text-core-slate-1100 block mb-50">
                  {t('printJobsPage.display.printer')}:
                </span>
                <p className="text-core-slate-1300">
                  {printerMap.get(selectedPrintJob.printerId)?.name ||
                    t('printJobsPage.display.unknown')}{' '}
                  (ID: {selectedPrintJob.printerId})
                </p>
              </div>

              <div>
                <span className="font-brand font-bold text-core-slate-1100 block mb-50">
                  {t('printJobsPage.display.label')}:
                </span>
                <p className="text-core-slate-1300">
                  {labelMap.get(selectedPrintJob.labelId)?.name ||
                    t('printJobsPage.display.unknown')}{' '}
                  (ID: {selectedPrintJob.labelId})
                </p>
              </div>

              <div>
                <span className="font-brand font-bold text-core-slate-1100 block mb-50">
                  {t('printJobsPage.display.labelRevision')}:
                </span>
                <p className="text-core-slate-1300">{selectedPrintJob.labelRevision || '-'}</p>
              </div>

              <div>
                <span className="font-brand font-bold text-core-slate-1100 block mb-50">
                  {t('printJobsPage.display.quantity')}:
                </span>
                <p className="text-core-slate-1300">{selectedPrintJob.quantity}</p>
              </div>

              <div>
                <span className="font-brand font-bold text-core-slate-1100 block mb-50">
                  {t('printJobsPage.display.createdBy')}:
                </span>
                <CreatedByDisplay oid={selectedPrintJob.createdBy} />
              </div>

              {selectedPrintJob.variables && Object.keys(selectedPrintJob.variables).length > 0 && (
                <div>
                  <span className="font-brand font-bold text-core-slate-1100 block mb-50">
                    {t('printJobsPage.display.variables')}:
                  </span>
                  <div className="bg-core-slate-50 p-200 rounded-100">
                    <pre className="text-xs text-core-slate-1300 whitespace-pre-wrap">
                      {JSON.stringify(selectedPrintJob.variables, null, 2)}
                    </pre>
                  </div>
                </div>
              )}

              {selectedPrintJob.preview && (
                <div>
                  <span className="font-brand font-bold text-core-slate-1100 block mb-50">
                    Preview:
                  </span>
                  <div className="max-h-[400px] overflow-auto bg-core-slate-50 p-200 rounded-100">
                    <img
                      src={`data:image/png;base64,${selectedPrintJob.preview}`}
                      alt="Label preview"
                      className="max-w-full h-auto mx-auto"
                    />
                  </div>
                </div>
              )}

              {selectedPrintJob.errorMessage && (
                <div>
                  <span className="font-brand font-bold text-core-slate-1100 block mb-50">
                    {t('printJobsPage.display.errorMessage')}:
                  </span>
                  <div className="bg-red-50 p-200 rounded-100">
                    <p className="text-red-800 text-xs">{selectedPrintJob.errorMessage}</p>
                  </div>
                </div>
              )}

              {!isAppTeamUser && (
                <div>
                  <span className="font-brand font-bold text-core-slate-1100">ID:</span>
                  <p className="text-core-slate-1300">{selectedPrintJob.id}</p>
                </div>
              )}

              <div>
                <span className="font-brand font-bold text-core-slate-1100">
                  {t('printJobsPage.display.createdAt')}:
                </span>
                <p className="text-core-slate-1300">
                  {new Date(selectedPrintJob.createdAt).toLocaleString()}
                </p>
              </div>

              <div>
                <span className="font-brand font-bold text-core-slate-1100">
                  {t('printJobsPage.display.lastUpdated')}:
                </span>
                <p className="text-core-slate-1300">
                  {new Date(selectedPrintJob.updatedAt).toLocaleString()}
                </p>
              </div>
            </div>
          )}
        </Modal.Content>
        <Modal.Footer>
          <Button variant="neutral" size="small" onClick={handleCloseDialog}>
            {t('printJobsPage.action.close')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
